import React, {useEffect, useRef} from 'react';
import {InfoAlert} from '../../../components/alerts/Info';
import {SuccessAlert} from '../../../components/alerts/Success';
import {ErrorAlert} from '../../../components/alerts/Error';
import {imgUrl, thumbnail} from '../../../utils/images';
import DynamicImage from '../../../components/DynamicImage';
import path from 'path-browserify';
import {CalendarIcon, MapPinIcon, PhotoIcon, SparklesIcon, TrashIcon,} from '@heroicons/react/24/outline';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {rectSortingStrategy, SortableContext, sortableKeyboardCoordinates,} from '@dnd-kit/sortable';
import {Story} from '../../../schema';
import {Dialog} from '@headlessui/react';
import {Button} from '../../../components/Button';
import {useEditStoryForm} from '../../../hooks/editTrip/useEditStoryForm';
import {DateTimePicker} from '../../../components/inputs/DateTimePicker';

export interface EditStoryModalProps {
  story: Story | null;
  tripId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
  onStoryCreated?: (story: Story) => void;
}

export const EditStoryModal = (props: EditStoryModalProps) => {
  const { story, tripId, isOpen, onClose, onStoryCreated } = props;

  // Derive mode from story (null = create, non-null = edit)
  const mode = story === null ? 'create' : 'edit';

  // All business logic is now in the hook
  const {
    storyDateTime,
    storyPlace,
    storyTitle,
    storyDescription,
    setStoryDateTime,
    setStoryPlace,
    setStoryTitle,
    setStoryDescription,
    uploadedMedia,
    uploadingMediaCount,
    handleMediaUpload,
    reorderMedia,
    deleteMedia,
    confirmingDelete,
    setConfirmingDelete,
    confirmingDeleteStory,
    setConfirmingDeleteStory,
    createStory,
    updateStory,
    deleteStory,
    hasFormChanges,
    isCreatingStory,
    patchStoryLoading,
    deleteStoryLoading,
    alertState,
    addPhotoTileStyle,
    validationErrors,
    validateField,
    validateAllFields,
    isFormValid,
  } = useEditStoryForm(story, tripId, onClose);

  // Handle create story and transition to edit mode
  const handleCreateAndContinue = async () => {
    // Validate all fields first
    const isValid = validateAllFields();
    if (!isValid) {
      // Validation errors are now visible, don't proceed
      return;
    }

    // If valid, create the story
    const newStory = await createStory();
    if (newStory && onStoryCreated) {
      onStoryCreated(newStory);
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  // DnD sensors - separate handling for desktop (mouse) and mobile (touch)
  const sensors = useSensors(
    useSensor(PointerSensor), // Desktop - immediate drag with mouse
    useSensor(TouchSensor, {
      // Mobile - requires press-and-hold to activate drag
      activationConstraint: {
        delay: 250, // 250ms hold before drag activates (prevents scroll conflict)
        tolerance: 5, // Allow 5px movement during delay without canceling
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = uploadedMedia.findIndex(
        (item) => item.fileUpload?.uploadUrl.pathname === active.id,
      );
      const newIndex = uploadedMedia.findIndex(
        (item) => item.fileUpload?.uploadUrl.pathname === over?.id,
      );
      reorderMedia(oldIndex, newIndex);
    }
  };

  const handleDeleteClick = (mediaId: string) => {
    setConfirmingDelete(mediaId);
  };

  const handleCancelDelete = () => {
    setConfirmingDelete(null);
  };

  // Handle clicking outside to cancel confirmation
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (confirmingDelete) {
        const target = event.target as Element;
        if (!target.closest('.confirmation-overlay')) {
          setConfirmingDelete(null);
        }
      }
    };

    if (confirmingDelete) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [confirmingDelete]);

  const handleDeleteStoryClick = () => {
    setConfirmingDeleteStory(true);
  };

  const handleCancelDeleteStory = () => {
    setConfirmingDeleteStory(false);
  };

  return (
    <Dialog open={props.isOpen} onClose={props.onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-4xl rounded-2xl bg-surface shadow-2xl overflow-hidden">
            <div className="bg-primary border-brand px-8 py-6">
              <Dialog.Title className="text-2xl  text-on-dark">
                {mode === 'create' ? 'Create Story' : 'Edit Story'}
              </Dialog.Title>
            </div>

            {/* Form Content with Better Spacing */}
            <div className="px-8 py-6 space-y-6">
              {/* Alerts Section */}
              {alertState === 'uploading' && uploadingMediaCount && uploadingMediaCount > 0 && (
                <InfoAlert text={`Uploading ${uploadingMediaCount} files...`} />
              )}
              {alertState === 'success' && <SuccessAlert text="Upload done" />}
              {alertState === 'error' && <ErrorAlert text="Upload failed" />}

              {/* Story Details Card */}
              <div className="bg-muted rounded-xl p-6 space-y-5 border border-subtle shadow-sm">
                <h3 className="text-lg  text-main flex items-center gap-2">
                  <span className="text-xl">✍️</span>
                  Story Details
                </h3>

                {/* Date & Time Input */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm  text-main">
                    <CalendarIcon className="w-4 h-4 text-brand" />
                    When did this happen?
                  </label>
                  <DateTimePicker
                    value={storyDateTime}
                    onChange={setStoryDateTime}
                    dateInputId="story-date"
                    timeInputId="story-time"
                    error={validationErrors.date}
                    onBlur={() => validateField('date')}
                  />
                </div>

                {/* Location Input with Icon */}
                <div className="space-y-2">
                  <label className="flex items-center justify-between text-sm  text-main">
                    <span className="flex items-center gap-2">
                      <MapPinIcon className="w-4 h-4 text-brand" />
                      Where were you?
                    </span>
                    {storyPlace && (
                      <span className="text-xs text-muted">{storyPlace.length}/50</span>
                    )}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MapPinIcon className="h-5 w-5 text-disabled" />
                    </div>
                    <input
                      id={'story-place'}
                      name={'story-place'}
                      type={'text'}
                      placeholder="e.g., Paris, France"
                      value={storyPlace}
                      onChange={(event) => setStoryPlace(event.target.value)}
                      onBlur={() => validateField('place')}
                      className={`block w-full pl-10 pr-3 py-3 rounded-lg bg-surface border
                        text-main placeholder:text-disabled
                        focus:outline-none focus:ring-2 focus:border-transparent
                        transition-all shadow-sm hover:border-interactive-hover
                        ${
                          validationErrors.place
                            ? 'border-error focus:ring-error'
                            : 'border-interactive focus:ring-brand'
                        }`}
                    />
                  </div>
                  {validationErrors.place && (
                    <p className="text-sm text-error mt-1">{validationErrors.place}</p>
                  )}
                </div>

                {/* Title Input with Icon */}
                <div className="space-y-2">
                  <label className="flex items-center justify-between text-sm  text-main">
                    <span className="flex items-center gap-2">
                      <SparklesIcon className="w-4 h-4 text-brand" />
                      Give it a title <span className="text-error">*</span>
                    </span>
                    <span
                      className={`text-xs ${storyTitle.length > 180 ? 'text-error ' : 'text-muted'}`}
                    >
                      {storyTitle.length}/200
                    </span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SparklesIcon className="h-5 w-5 text-disabled" />
                    </div>
                    <input
                      id={'story-title'}
                      name={'story-title'}
                      type={'text'}
                      placeholder="e.g., Sunny Breakfast ☀️"
                      value={storyTitle}
                      onChange={(event) => setStoryTitle(event.target.value)}
                      onBlur={() => validateField('title')}
                      className={`block w-full pl-10 pr-3 py-3 rounded-lg bg-surface border
                        text-main placeholder:text-disabled 
                        focus:outline-none focus:ring-2 focus:border-transparent
                        transition-all shadow-sm hover:border-interactive-hover
                        ${
                          validationErrors.title
                            ? 'border-error focus:ring-error'
                            : 'border-interactive focus:ring-brand'
                        }`}
                    />
                  </div>
                  {validationErrors.title && (
                    <p className="text-sm text-error mt-1">{validationErrors.title}</p>
                  )}
                </div>

                {/* Description Textarea */}
                <div className="space-y-2">
                  <label className="flex items-center justify-between text-sm  text-main">
                    <span className="flex items-center gap-2">
                      <span className="text-base">📝</span>
                      Tell your story
                    </span>
                    {storyDescription && (
                      <span
                        className={`text-xs ${(storyDescription?.length ?? 0) > 1900 ? 'text-error ' : 'text-muted'}`}
                      >
                        {storyDescription.length}/2000
                      </span>
                    )}
                  </label>
                  <textarea
                    id={'story-description'}
                    name={'description'}
                    rows={5}
                    placeholder="We started this sunny morning ❤️ at a lovely Cafè in Florence 🥐☕️"
                    value={storyDescription}
                    onChange={(event) => setStoryDescription(event.target.value)}
                    onBlur={() => validateField('description')}
                    className={`block w-full px-4 py-3 rounded-lg bg-surface border
                      text-main placeholder:text-disabled
                      focus:outline-none focus:ring-2 focus:border-transparent
                      transition-all shadow-sm hover:border-interactive-hover resize-none
                      ${
                        validationErrors.description
                          ? 'border-error focus:ring-error'
                          : 'border-interactive focus:ring-brand'
                      }`}
                  />
                  {validationErrors.description && (
                    <p className="text-sm text-error mt-1">{validationErrors.description}</p>
                  )}
                </div>
              </div>

              {/* Photo Gallery Card - Only show in edit mode */}
              {mode === 'edit' && (
                <div className="bg-muted border-subtle rounded-xl p-6 space-y-4 border shadow-sm">
                  <h3 className="text-lg  text-main flex items-center gap-2">
                    <PhotoIcon className="w-5 h-5 text-brand" />
                    Photos
                    {uploadedMedia.length > 0 && (
                      <span className="text-sm font-normal text-muted">
                        ({uploadedMedia.length} {uploadedMedia.length === 1 ? 'photo' : 'photos'})
                      </span>
                    )}
                  </h3>

                  <div className="flex flex-wrap gap-3 justify-center">
                    {/* Add Photo Button */}
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="group rounded-2xl border-2 border-dashed border-brand bg-white/50
                      hover:bg-surface hover:border-brand hover:shadow-lg
                      flex items-center justify-center cursor-pointer transition-all duration-300
                      transform hover:scale-105"
                      style={{
                        height: addPhotoTileStyle.height,
                        width: addPhotoTileStyle.width,
                      }}
                    >
                      <div className="text-center text-brand group-hover:text-brand">
                        <PhotoIcon className="w-10 h-10 mx-auto mb-2 opacity-70 group-hover:opacity-100 transition-opacity" />
                        <div className="text-sm ">Add Photos</div>
                        <div className="text-xs text-muted mt-1">Click to upload</div>
                      </div>
                    </div>

                    {/* Photo Grid with Drag & Drop */}
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={uploadedMedia
                          .filter((media) => media.fileUpload && media.success)
                          .map((media) => media.fileUpload?.uploadUrl.pathname || '')}
                        strategy={rectSortingStrategy}
                      >
                        {uploadedMedia
                          .filter((media) => media.fileUpload)
                          .map((media) => {
                            const pathname = media.fileUpload?.uploadUrl.pathname || '';
                            const isPlaceholder = !media.success;
                            return (
                              <div key={pathname} className="relative group">
                                <DynamicImage
                                  id={pathname}
                                  className="max-h-[200px] w-auto max-w-[28vw] md:max-w-[40vw] rounded-2xl object-cover
                              shadow-md hover:shadow-xl transition-shadow border-2 border-surface"
                                  src={
                                    isPlaceholder
                                      ? '/spinner.gif'
                                      : imgUrl(tripId || '', thumbnail(path.basename(pathname)))
                                  }
                                  defaultSrc={'/spinner.gif'}
                                  sortable={!isPlaceholder}
                                />
                                {!isPlaceholder && (
                                  <>
                                    {confirmingDelete === pathname ? (
                                      <div
                                        className="confirmation-overlay absolute inset-0 bg-black/70 backdrop-blur-sm
                                flex items-center justify-center rounded-2xl"
                                      >
                                        <div className="flex flex-col gap-2 p-4">
                                          <p className="text-on-dark  text-center mb-2">
                                            Delete this photo?
                                          </p>
                                          <div className="flex gap-2">
                                            <Button
                                              variant="destructive"
                                              size="sm"
                                              onClick={() => deleteMedia(pathname)}
                                            >
                                              Delete
                                            </Button>
                                            <Button
                                              variant="secondary"
                                              size="sm"
                                              onClick={handleCancelDelete}
                                            >
                                              Cancel
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => handleDeleteClick(pathname)}
                                        className="absolute top-2 right-2 p-2 rounded-full opacity-0 group-hover:opacity-100 hover:scale-110"
                                      >
                                        <TrashIcon className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            );
                          })}
                      </SortableContext>
                    </DndContext>
                  </div>
                </div>
              )}
            </div>

            {/* Modern Action Bar */}
            <div className="bg-muted border-subtle px-8 py-5">
              {mode === 'create' ? (
                // Create mode: Simple buttons
                <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
                  <Button
                    variant="primary"
                    size="lg"
                    className="flex-1"
                    disabled={isCreatingStory}
                    loading={isCreatingStory}
                    onClick={handleCreateAndContinue}
                  >
                    {isCreatingStory ? 'Creating...' : 'Next: Add Photos'}
                  </Button>

                  <Button variant="secondary" disabled={isCreatingStory} onClick={onClose}>
                    Cancel
                  </Button>
                </div>
              ) : confirmingDeleteStory ? (
                <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
                  <div className="flex items-center gap-2 text-error  mb-2 sm:mb-0">
                    <TrashIcon className="w-5 h-5" />
                    <span>Are you sure?</span>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="destructive"
                      disabled={patchStoryLoading || deleteStoryLoading}
                      loading={deleteStoryLoading}
                      onClick={deleteStory}
                    >
                      {deleteStoryLoading ? 'Deleting...' : 'Yes, Delete Story'}
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={patchStoryLoading || deleteStoryLoading}
                      onClick={handleCancelDeleteStory}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
                  <Button
                    variant="primary"
                    size="lg"
                    className="flex-1"
                    disabled={
                      patchStoryLoading ||
                      deleteStoryLoading ||
                      !hasFormChanges ||
                      (uploadingMediaCount ?? 0) > 0
                    }
                    loading={patchStoryLoading}
                    onClick={updateStory}
                  >
                    {patchStoryLoading ? 'Saving...' : 'Save Changes'}
                  </Button>

                  <Button
                    variant="secondary"
                    disabled={patchStoryLoading || deleteStoryLoading}
                    onClick={props.onClose}
                  >
                    Cancel
                  </Button>

                  <Button
                    variant="destructive"
                    disabled={patchStoryLoading || deleteStoryLoading}
                    onClick={handleDeleteStoryClick}
                    title="Delete Story"
                    iconBefore={<TrashIcon className="w-5 h-5" />}
                    className="group"
                  >
                    <span>Delete Story</span>
                  </Button>
                </div>
              )}
            </div>

            <input
              multiple={true}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={(event) => {
                const files = event.target.files;
                if (files) {
                  handleMediaUpload(Array.from(files));
                }
              }}
            />
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};
